import { useAsyncData, useRouter } from '#imports'
import { type Ref, computed } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'

import { PRODUCT_ROUTES } from '~/scopes/product-core/route-names'

import type { RatingValue, TableData } from '../../models/productComparison'
import {
  type ProductSpecification,
  buildComparisonTable,
} from '../../utils/comparisonHelper'

import translations from './useEnrichResultsPage.translations'
import type {
  ComparisonData,
  ReviewRating,
  Variants,
} from './useEnrichResultsPage.types'

export function useEnrichResultsPage(
  comparisonData: Ref<ComparisonData | null | undefined>,
  comparisonPending: Ref<boolean>,
) {
  const $httpFetch = useHttpFetch()
  const i18n = useI18n()
  const router = useRouter()

  function productPageUrl(slug: string) {
    return router.resolve({
      name: PRODUCT_ROUTES.MASTER_PRODUCT_PAGE,
      params: { locale: toBcp47Locale(i18n.currentLocale), slug },
      hash: '#reviews',
    }).href
  }

  function productVariantUrl(slug: string, productId: string) {
    if (!slug) return ''

    return router.resolve({
      name: PRODUCT_ROUTES.VARIANT_PRODUCT_PAGE,
      params: {
        locale: toBcp47Locale(i18n.currentLocale),
        slugV2: slug,
        uuid: productId,
      },
    }).href
  }

  async function fetchReviews(productId: string): Promise<ReviewRating | null> {
    try {
      return (await $httpFetch(
        '/reviews/v1/products/:productId/reviews-summary',
        { pathParams: { productId } },
      )) as ReviewRating
    } catch {
      return null
    }
  }

  async function fetchVariants(productId: string): Promise<Variants | null> {
    try {
      return await $httpFetch('/product-page/products/:productId/pickers', {
        pathParams: { productId },
        query: {},
      })
    } catch {
      return null
    }
  }

  async function fetchTechnicalSpecs(
    productId: string,
  ): Promise<ProductSpecification[]> {
    const response = await $httpFetch(
      '/product-page/products/:productId/technical_specifications',
      { pathParams: { productId } },
    )

    return (response.specifications ?? []) as ProductSpecification[]
  }

  const products = computed(() => comparisonData.value?.products ?? [])
  const productIds = computed(() => products.value.map((p) => p.productId))
  const cacheKey = computed(() => productIds.value.join('-'))

  const { data: allReviews, pending: reviewsPending } = useAsyncData(
    () => `reviews-${cacheKey.value}`,
    () => Promise.all(productIds.value.map(fetchReviews)),
    { default: () => [] as (ReviewRating | null)[], watch: [cacheKey] },
  )

  const { data: allSpecs, pending: specsPending } = useAsyncData(
    () => `specs-${cacheKey.value}`,
    () => Promise.all(productIds.value.map(fetchTechnicalSpecs)),
    { default: () => [] as ProductSpecification[][], watch: [cacheKey] },
  )

  const { data: allVariants, pending: variantsPending } = useAsyncData(
    () => `variants-${cacheKey.value}`,
    () => Promise.all(productIds.value.map(fetchVariants)),
    { default: () => [] as (Variants | null)[], watch: [cacheKey] },
  )

  const pending = computed(
    () =>
      comparisonPending.value ||
      reviewsPending.value ||
      specsPending.value ||
      variantsPending.value,
  )

  const enrichedProducts = computed(() =>
    products.value.map((product, i) => ({
      product,
      review: allReviews.value[i] ?? undefined,
      specifications: allSpecs.value[i] ?? [],
      variants: allVariants.value[i] ?? undefined,
    })),
  )

  const ratings = computed<RatingValue[]>(() => {
    if (!enrichedProducts.value.length) return []

    const hasAnyReview = enrichedProducts.value.some((p) => p.review != null)
    if (!hasAnyReview) return []

    return enrichedProducts.value.map((p) => ({
      averageRate: p.review?.averageRate ?? null,
      count: p.review?.count ?? null,
      url: productPageUrl(p.product.slug),
    }))
  })

  const tableData = computed<TableData[]>(() => {
    if (!enrichedProducts.value.length) return []

    return buildComparisonTable({
      textBlocks: comparisonData.value?.textBlocks ?? [],
      specs: enrichedProducts.value.map((p) => p.specifications),
      scores: enrichedProducts.value.map((p) =>
        Object.fromEntries(
          p.product.scores.map((s) => [s.id, String(s.value)]),
        ),
      ),
      variants: enrichedProducts.value.map(
        (p) => p.variants?.pickerGroups ?? null,
      ),
      buildVariantUrl: productVariantUrl,
      labels: {
        introduction: i18n(translations.introduction),
        availableModels: i18n(translations.availableModels),
        conclusion: i18n(translations.conclusion),
        scores: i18n(translations.scores),
        durability: i18n(translations.durability),
        performance: i18n(translations.performance),
        screenQuality: i18n(translations.screenQuality),
        soundQuality: i18n(translations.soundQuality),
        multimedia: i18n(translations.multimedia),
        camera: i18n(translations.camera),
        miscelaneous: i18n(translations.miscelaneous),
      },
    })
  })

  return { products, ratings, tableData, pending }
}
